import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Star, FileText, Briefcase, MessageSquare, Users, Shield, Download, Mail, Phone, ExternalLink, MailCheck, MailX, MailQuestion, Send } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "quotes", label: "Quotes", icon: FileText, path: "/admin/quotes" },
  { id: "applications", label: "Applications", icon: Briefcase, path: "/admin/applications" },
  { id: "contacts", label: "Messages", icon: MessageSquare, path: "/admin/contacts" },
  { id: "reviews", label: "Reviews", icon: Star, path: "/admin/reviews" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("quotes");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const { data: res } = await api.post("/test-email");
      if (res.delivered) {
        toast.success(`Test email sent to ${res.to} — check the inbox AND spam folder.`);
      } else {
        toast.error(`Email not sent: ${res.reason || "unknown error"}`);
      }
    } catch (e) {
      toast.error("Test email request failed");
    } finally {
      setTestingEmail(false);
    }
  };

  const loadTab = async (tabId) => {
    setLoading(true);
    try {
      const t = TABS.find((x) => x.id === tabId);
      const { data: res } = await api.get(t.path);
      setData((prev) => ({ ...prev, [tabId]: res }));
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data: res } = await api.get("/admin/stats");
      setStats(res);
    } catch {}
  };

  useEffect(() => {
    loadStats();
    loadTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!data[tab]) loadTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
  };

  const items = data[tab] || [];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Admin topbar */}
      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-10 h-10 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-600 border-2 border-black" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display font-black uppercase text-white text-sm">Marco Polo</span>
              <span className="font-condensed text-[10px] tracking-[0.22em] uppercase text-red-500 font-semibold">Admin Console</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-slate-400 hover:text-white font-condensed uppercase tracking-wider text-xs"
              data-testid="admin-view-site-link"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View Site
            </Link>
            <div className="hidden md:flex items-center gap-2 text-xs text-slate-400 font-condensed uppercase tracking-widest border border-white/10 rounded-sm px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {user?.email}
            </div>
            <button
              onClick={sendTestEmail}
              disabled={testingEmail}
              data-testid="admin-send-test-email"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500/60 hover:text-red-400 text-slate-300 px-4 py-2 rounded-sm font-condensed uppercase tracking-wider font-bold text-sm transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> {testingEmail ? "Sending…" : "Test Email"}
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-black px-4 py-2 rounded-sm font-condensed uppercase tracking-wider font-bold text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-2">— Dashboard</p>
            <h1 className="font-display font-black text-3xl md:text-5xl uppercase leading-[1]">
              Operations Console<span className="text-red-500">.</span>
            </h1>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="admin-stats">
          <StatCard icon={FileText} label="Quotes" value={stats?.quotes} />
          <StatCard icon={Briefcase} label="Applications" value={stats?.applications} />
          <StatCard icon={MessageSquare} label="Messages" value={stats?.contacts} />
          <StatCard icon={Star} label="Reviews" value={stats?.reviews} accent />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-white/10 mb-6" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              role="tab"
              aria-selected={tab === t.id}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 font-condensed uppercase tracking-wider text-sm font-bold border-b-2 -mb-px transition-colors",
                tab === t.id
                  ? "border-red-600 text-white"
                  : "border-transparent text-slate-500 hover:text-white"
              )}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {stats?.[t.id === "contacts" ? "contacts" : t.id] != null && (
                <span className="text-xs text-slate-500">({stats[t.id === "contacts" ? "contacts" : t.id]})</span>
              )}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden" data-testid="admin-table">
          {loading ? (
            <div className="p-10 text-center text-slate-400 font-condensed uppercase tracking-widest text-xs">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              <Users className="w-6 h-6 mx-auto mb-3 opacity-60" />
              <p className="font-condensed uppercase tracking-widest text-xs">No {tab} yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((item) => (
                <Row key={item.id || item._id} tab={tab} item={item} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={cn(
      "relative bg-[#0A0A0A] border rounded-sm p-5",
      accent ? "border-red-600/40" : "border-white/10"
    )}>
      <div className={cn(
        "absolute top-0 left-0 h-0.5 w-full",
        accent ? "bg-red-600" : "bg-white/10"
      )} />
      <div className="flex items-center justify-between mb-2">
        <span className="font-condensed text-[10px] tracking-[0.28em] uppercase text-slate-400 font-bold">{label}</span>
        <Icon className={cn("w-4 h-4", accent ? "text-red-500" : "text-slate-500")} />
      </div>
      <div className="font-display font-black text-3xl md:text-4xl text-white leading-none">
        {value != null ? value : "—"}
      </div>
    </div>
  );
}

function Row({ tab, item }) {
  const date = item.created_at ? new Date(item.created_at).toLocaleString() : "";

  if (tab === "quotes") {
    return (
      <RowShell date={date} item={item}>
        <div className="font-display font-black text-white uppercase">{item.first_name} {item.last_name}</div>
        <ContactLine email={item.email} phone={item.phone} />
        <KV k="Company" v={item.company_name} />
        <KV k="Service" v={item.service_needed} />
        {item.preferred_date && <KV k="Preferred Date" v={new Date(item.preferred_date).toLocaleDateString()} />}
        {item.message && <blockquote className="text-slate-300 border-l-2 border-red-600/60 pl-3 mt-2">{item.message}</blockquote>}
      </RowShell>
    );
  }

  if (tab === "applications") {
    return (
      <RowShell date={date} item={item}>
        <div className="font-display font-black text-white uppercase">{item.first_name} {item.last_name}</div>
        <ContactLine email={item.email} phone={item.phone} />
        <KV k="Address" v={`${item.street_address}${item.apartment ? ", Apt " + item.apartment : ""}, ${item.city}, ${item.state} ${item.zip_code}`} />
        <KV k="Experience" v={item.has_security_experience} />
        <KV k="Shift" v={item.shift_availability} />
        {item.resume_filename && (
          <KV
            k="Resume"
            v={
              <a
                href={`${API}/admin/applications/${item.id}/resume`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 underline underline-offset-2"
              >
                <Download className="w-3.5 h-3.5" /> {item.resume_filename}
              </a>
            }
          />
        )}
        {item.message && <blockquote className="text-slate-300 border-l-2 border-red-600/60 pl-3 mt-2">{item.message}</blockquote>}
      </RowShell>
    );
  }

  if (tab === "contacts") {
    return (
      <RowShell date={date} item={item}>
        <div className="font-display font-black text-white uppercase">{item.name}</div>
        <ContactLine email={item.email} phone={item.phone} />
        {item.message && <blockquote className="text-slate-300 border-l-2 border-red-600/60 pl-3 mt-2">{item.message}</blockquote>}
      </RowShell>
    );
  }

  if (tab === "reviews") {
    return (
      <RowShell date={date} item={item}>
        <div className="flex items-center justify-between gap-4">
          <div className="font-display font-black text-white uppercase">{item.name}</div>
          <div className="flex">
            {[1,2,3,4,5].map((n) => (
              <Star key={n} className={cn("w-4 h-4", (item.rating || 0) >= n ? "fill-amber-400 text-amber-400" : "text-slate-700")} />
            ))}
          </div>
        </div>
        {item.email && <ContactLine email={item.email} />}
        {item.comment && <blockquote className="text-slate-300 border-l-2 border-red-600/60 pl-3 mt-2">{item.comment}</blockquote>}
      </RowShell>
    );
  }

  return null;
}

function RowShell({ children, date, item }) {
  return (
    <div className="p-5 hover:bg-white/[0.02] transition-colors">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex-1 space-y-1.5">{children}</div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className="font-condensed text-[10px] uppercase tracking-widest text-slate-500">{date}</span>
          <EmailBadge item={item} />
        </div>
      </div>
    </div>
  );
}

function EmailBadge({ item }) {
  if (!item) return null;
  if (item.email_delivered === true) {
    return (
      <span data-testid="email-status-sent" className="inline-flex items-center gap-1 text-[10px] font-condensed uppercase tracking-widest text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded-sm px-2 py-0.5">
        <MailCheck className="w-3 h-3" /> Email Sent
      </span>
    );
  }
  if (item.email_delivered === false) {
    return (
      <span data-testid="email-status-failed" title={item.email_error || "Email failed"} className="inline-flex items-center gap-1 text-[10px] font-condensed uppercase tracking-widest text-red-400 border border-red-500/30 bg-red-500/10 rounded-sm px-2 py-0.5">
        <MailX className="w-3 h-3" /> Failed{item.email_error ? `: ${item.email_error}` : ""}
      </span>
    );
  }
  return (
    <span data-testid="email-status-unknown" className="inline-flex items-center gap-1 text-[10px] font-condensed uppercase tracking-widest text-slate-500 border border-white/10 rounded-sm px-2 py-0.5">
      <MailQuestion className="w-3 h-3" /> Email N/A
    </span>
  );
}

function ContactLine({ email, phone }) {
  return (
    <div className="flex flex-wrap gap-3 text-sm text-slate-400">
      {email && (
        <a href={`mailto:${email}`} className="inline-flex items-center gap-1 hover:text-white">
          <Mail className="w-3.5 h-3.5" /> {email}
        </a>
      )}
      {phone && (
        <a href={`tel:${phone}`} className="inline-flex items-center gap-1 hover:text-white">
          <Phone className="w-3.5 h-3.5" /> {phone}
        </a>
      )}
    </div>
  );
}

function KV({ k, v }) {
  if (v == null || v === "") return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="font-condensed uppercase tracking-widest text-[10px] text-slate-500 w-28 shrink-0 pt-0.5">{k}</span>
      <span className="text-slate-200 flex-1">{v}</span>
    </div>
  );
}
